// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { Badge } from "@/components/ui/badge"
import { cn } from "@sglara/cn"

// ADR-0022: four canonical statuses (running/completed/failed/expired); every
// value has a defined colour — no fallback to an unknown-state rendering
// (PRD §AC: "no unknown-state fallback for `expired`"). Label is the canonical
// value itself, rendered capitalised.
// Restyled onto "Univers Lagon" tokens: coloured dot + tinted pill; dot pulses
// only while `running`.
// `data-status` exposes the raw enum value so component tests can assert on
// status without coupling to Tailwind class names.
//
// Exported so other renderings of the same four statuses (e.g. the dashboard's
// `StatusPill`) share this style source instead of forking their own copy.
export const STATUS_STYLE: Record<
  CycleStatus,
  { pill: string; dot: string; pulse: boolean }
> = {
  running: {
    pill: "bg-running/12 text-running-fg border-running/40",
    dot: "bg-running shadow-[0_0_8px_var(--running)]",
    pulse: true,
  },
  completed: {
    pill: "bg-done/12 text-done-fg border-done/40",
    dot: "bg-done",
    pulse: false,
  },
  failed: {
    pill: "bg-failed/12 text-failed-fg border-failed/40",
    dot: "bg-failed",
    pulse: false,
  },
  expired: {
    pill: "bg-expired/12 text-expired-fg border-expired/40",
    dot: "bg-expired",
    pulse: false,
  },
}

export const StatusDot = ({ status }: { status: CycleStatus }) => {
  const style = STATUS_STYLE[status]
  return (
    <span
      aria-hidden
      className={cn(
        "size-1.5 rounded-full",
        style.dot,
        style.pulse && "animate-pulse-dot",
      )}
    />
  )
}

export const CycleStatusBadge = ({ status }: { status: CycleStatus }) => {
  const style = STATUS_STYLE[status]
  return (
    <Badge
      role="status"
      aria-label={`Cycle status: ${status}`}
      data-status={status}
      className={cn(
        "gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-medium capitalize",
        style.pill,
      )}
    >
      <StatusDot status={status} />
      {status}
    </Badge>
  )
}
